import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import {
  DictionaryTermApiProvider,
  useDictionaryTermApi,
} from '../Services/DictionaryTermApiProvider';
import {
  TaxonomyDictionaryApiProvider,
  useTaxonomyDictionaryApi,
} from '../Services/TaxonomyDictionaryApiProvider';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { CustomInput, CustomToggle } from '../Components/CustomFormElements';
import PageLoader from '../Components/PageLoader';
import { isEmpty } from '../utils/validationUtil';

import EditableItemList from '../Components/EditableItemList';

import '../Components/TaxonomyDictionary.css';

function TaxonomyDictionaryContent({ canEdit }) {
  const { organizationId, dictionaryId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getTaxonomyDictionaryById, createTaxonomyDictionary, updateTaxonomyDictionary } =
    useTaxonomyDictionaryApi();

  const { getDictionaryTerms, createDictionaryTerm, updateDictionaryTerm, deleteDictionaryTerm } =
    useDictionaryTermApi();

  const [loading, setLoading] = useState(false);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  const [terms, setTerms] = useState([]);
  const [hasMoreTerms, setHasMoreTerms] = useState(false);
  const [totalTerms, setTotalTerms] = useState(0);

  useEffect(() => {
    const fetchDictionary = async () => {
      setLoading(true);
      try {
        if (dictionaryId !== 'new-dictionary') {
          const response = await getTaxonomyDictionaryById(dictionaryId);
          setTitle(response.title);
          setDescription(response.description || '');
          setIsActive(response.is_active);
          setIsNew(false);
          setIsDisabled(!canEdit);

          try {
            const termsResponse = await getDictionaryTerms(dictionaryId, 0, 100);
            setTerms(termsResponse.items.map((term) => ({ ...term, _isNew: false })));
            setTotalTerms(termsResponse.totalCount);
            setHasMoreTerms(termsResponse.totalCount > termsResponse.items.length);
          } catch {
            showAlert('fail', 'Failed to load terms');
          }
        } else {
          setIsNew(true);
          setIsDisabled(!canEdit);
          setTerms([]);
        }
      } catch {
        showAlert('fail', 'Failed to load dictionary');
      } finally {
        setLoading(false);
      }
    };

    fetchDictionary();
  }, [dictionaryId, canEdit]);

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/taxonomy-dictionary`);
  };

  const onSave = async () => {
    if (isEmpty(title)) {
      showAlert('fail', 'Please enter a name');
      return;
    }

    const data = { title, description, is_active: isActive };

    setLoading(true);
    try {
      if (isNew) {
        await createTaxonomyDictionary(data);
        showAlert('success', 'Dictionary created!');
        navigate(`/organizations/${organizationId}/taxonomy-dictionary`);
      } else {
        await updateTaxonomyDictionary(dictionaryId, data);

        // Save all new terms
        for (const term of terms) {
          if (term._isNew && !isEmpty(term.keyword)) {
            await createDictionaryTerm(dictionaryId, {
              keyword: term.keyword,
              definition: term.definition,
              is_active: term.is_active,
            });
          } else if (!term._isNew && term._modified) {
            await updateDictionaryTerm(dictionaryId, term.id, {
              keyword: term.keyword,
              definition: term.definition,
              is_active: term.is_active,
            });
          }
        }

        showAlert('success', 'Dictionary updated!');

        // Refresh terms after save
        const termsResponse = await getDictionaryTerms(dictionaryId, 0, 100);
        setTerms(termsResponse.items.map((t) => ({ ...t, _isNew: false, _modified: false })));
        setTotalTerms(termsResponse.totalCount);
        setHasMoreTerms(termsResponse.totalCount > termsResponse.items.length);
      }
    } catch {
      showAlert('fail', 'Failed to save dictionary');
    } finally {
      setLoading(false);
    }
  };

  const addTerm = () => {
    setTerms([
      ...terms,
      {
        keyword: '',
        definition: '',
        is_active: true,
        _isNew: true,
        _tempId: Date.now(),
      },
    ]);
  };

  const removeTerm = (itemId) => {
    const termToDelete = terms.find((t) => (t.id || t._tempId) === itemId);

    if (!termToDelete) return;

    if (termToDelete._isNew) {
      // Just remove from local state if it's a new unsaved term
      setTerms((prevTerms) => prevTerms.filter((t) => (t.id || t._tempId) !== itemId));
    } else {
      // Show confirmation and delete from server
      showConfirmation('Are you sure you want to delete this term?', async () => {
        try {
          await deleteDictionaryTerm(dictionaryId, termToDelete.id);
          setTerms((prevTerms) => prevTerms.filter((t) => (t.id || t._tempId) !== itemId));
          showAlert('success', 'Term deleted successfully');
        } catch {
          showAlert('fail', 'Failed to delete term');
        }
      });
    }
  };

  const updateTerm = (itemId, field, value) => {
    setTerms((prevTerms) =>
      prevTerms.map((term) => {
        const currentId = term.id || term._tempId;
        if (currentId === itemId) {
          return {
            ...term,
            [field]: value,
            _modified: !term._isNew ? true : term._modified,
          };
        }
        return term;
      }),
    );
  };

  if (loading) return <PageLoader />;

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className="h2-title">{title || 'New Dictionary'}</h2>
          <div className="gap d-flex flex-row-to-col flex-column">
            <div className="int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr">
              <h4 className="h4-title">Main Settings</h4>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Dictionary Name</div>
                  <div className="small-font">Enter dictionary name</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="dictionary-name"
                    label="Dictionary Name"
                    placeholder="Enter dictionary name"
                    type="text"
                    icon={<i className="fa-solid fa-book"></i>}
                    value={title}
                    onChange={(id, value) => setTitle(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Description</div>
                  <div className="small-font">Optional description for this dictionary</div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomInput
                    id="dictionary-description"
                    label="Description"
                    placeholder="Enter description"
                    type="text"
                    value={description}
                    onChange={(id, value) => setDescription(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className="input-row input-block d-flex flex-row justify-between">
                <div className="input-description">
                  <div className="input-title">Activate Dictionary</div>
                  <div className="small-font">
                    Set dictionary to be active or inactive to enable or disable it
                  </div>
                </div>
                <div className="align-wrapper d-flex align-center">
                  <CustomToggle
                    id="toggle-status"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>

            {!isNew && (
              <EditableItemList
                itemName="Term"
                items={terms}
                totalItems={totalTerms}
                hasMoreItems={hasMoreTerms}
                isDisabled={isDisabled}
                updateItem={updateTerm}
                addItem={addTerm}
                removeItem={removeTerm}
                onViewAllClick={() =>
                  navigate(
                    `/organizations/${organizationId}/taxonomy-dictionary/${dictionaryId}/terms`,
                  )
                }
              />
            )}

            <div className="buttons-container d-flex justify-end">
              <Button variant="primary" size="medium" disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant="danger" size="medium" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TaxonomyDictionary({ canEdit }) {
  return (
    <ConfirmationProvider>
      <TaxonomyDictionaryApiProvider>
        <DictionaryTermApiProvider>
          <TaxonomyDictionaryContent canEdit={canEdit} />
        </DictionaryTermApiProvider>
      </TaxonomyDictionaryApiProvider>
    </ConfirmationProvider>
  );
}

export default TaxonomyDictionary;
